<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { HOME_PRODUCTS, productHref, staticPageHref, accountHref as accountSectionHref, swapLocaleInPath } from '@/router/products'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, setStoredLocale, type Locale } from '@/i18n'
import { useAuth } from '@/composables/useAuth'
import AppButton from '../ui/AppButton.vue'
import AppLogo from '../ui/AppLogo.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const { isAuthenticated } = useAuth()

const isOpen = ref(false)
const isScrolled = ref(false)
/** Évite le décalage d'hydratation : on ne reflète l'état connecté qu'après
 *  le montage côté client (le HTML pré-rendu suppose toujours « déconnecté »). */
const mounted = ref(false)

const currentLocale = computed<Locale>(() => route.meta.locale ?? DEFAULT_LOCALE)
const otherLocales = computed(() => SUPPORTED_LOCALES.filter((l) => l !== currentLocale.value))
const localeName = (l: Locale) => t('locale.name', l, { locale: l })

/** Lien vers chaque produit de la home (interne ou externe selon le produit). */
const productLinks = computed(() =>
  HOME_PRODUCTS.map((p) => ({
    key: p.key,
    label: t(`subscription.products.items.${p.key}`),
    href: p.externalHref ?? productHref(p.key, currentLocale.value),
    external: Boolean(p.externalHref),
  })),
)

/** Lien « espace personnel » : page de connexion par défaut, espace perso une
 *  fois connecté (après montage client, cf. `mounted`). */
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const accountHref = computed(() => staticPageHref('account', currentLocale.value))
const memberHref = computed(() =>
  mounted.value && isAuthenticated.value ? accountHref.value : loginHref.value,
)

/** Lien utilitaire « Contacts et aide » : connecté → formulaire interne ;
 *  visiteur non connecté → page de contact externe du client. */
const EXTERNAL_CONTACT = 'https://smerra.fr/contacts/'
const contactInternal = computed(() => mounted.value && isAuthenticated.value)
const contactHref = computed(() =>
  contactInternal.value ? accountSectionHref('contact', currentLocale.value) : EXTERNAL_CONTACT,
)

function close() { isOpen.value = false }
function toggle() { isOpen.value = !isOpen.value }

function changeLocale(target: Locale) {
  // Fixe la préférence avant de naviguer (sinon la garde router, lisant l'ancienne
  // préférence, ré-aligne aussitôt sur la langue précédente), puis `replace` pour
  // ne pas empiler une entrée d'historique au changement de langue.
  setStoredLocale(target)
  router.replace(swapLocaleInPath(route.path, target))
  close()
}

function onScroll() {
  isScrolled.value = window.scrollY > 8
}

onMounted(() => {
  mounted.value = true
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})
onUnmounted(() => window.removeEventListener('scroll', onScroll))

watch(isOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<template>
  <header :class="['header', isScrolled && 'header--scrolled']">
    <div class="container header__inner">
      <AppLogo />

      <div class="header__actions">
        <AppButton as="a" :href="memberHref" variant="dark" size="sm" ring>
          {{ t('common.memberArea') }}
        </AppButton>
        <button
          class="header__burger"
          :aria-expanded="isOpen"
          aria-controls="mobile-drawer"
          :aria-label="t('header.openMenu')"
          @click="toggle"
        >
          <span :class="['burger', isOpen && 'burger--open']">
            <span></span><span></span><span></span>
          </span>
        </button>
      </div>
    </div>

    <!-- Mobile drawer — téléporté vers <body> : le backdrop-filter du header en
         fait sinon le bloc conteneur du position:fixed (drawer écrasé à ~0 de
         haut) et l'enferme dans son contexte d'empilement (z-index 50). -->
    <Teleport to="body">
      <transition name="drawer">
        <div v-if="isOpen" id="mobile-drawer" class="drawer" role="dialog" aria-modal="true">
          <!-- Langue (en tête) -->
          <section class="drawer__section">
            <h2 class="drawer__section-title">{{ t('header.drawer.language') }}</h2>
            <div class="drawer__lang" role="group" :aria-label="t('topbar.switchLabel')">
              <button
                type="button"
                class="drawer__lang-btn drawer__lang-btn--active"
                :lang="currentLocale"
                aria-current="true"
                disabled
              >
                {{ localeName(currentLocale) }}
              </button>
              <button
                v-for="l in otherLocales"
                :key="l"
                type="button"
                class="drawer__lang-btn"
                :hreflang="l"
                :lang="l"
                @click="changeLocale(l)"
              >
                {{ localeName(l) }}
              </button>
            </div>
          </section>

          <!-- Produits -->
          <section class="drawer__section">
            <h2 class="drawer__section-title">{{ t('header.drawer.products') }}</h2>
            <ul class="drawer__list">
              <li v-for="p in productLinks" :key="p.key">
                <a
                  :href="p.href"
                  :target="p.external ? '_blank' : undefined"
                  :rel="p.external ? 'noopener' : undefined"
                  class="drawer__link"
                  @click="close"
                >{{ p.label }}</a>
              </li>
            </ul>
          </section>

          <!-- Compte & aide -->
          <section class="drawer__section">
            <h2 class="drawer__section-title">{{ t('header.drawer.account') }}</h2>
            <ul class="drawer__list">
              <li>
                <a :href="memberHref" class="drawer__link" @click="close">{{ t('header.memberAreaPersonal') }}</a>
              </li>
              <li>
                <a
                  :href="contactHref"
                  target="_blank"
                  rel="noopener"
                  class="drawer__link"
                  @click="close"
                >{{ t('topbar.contact') }}</a>
              </li>
            </ul>
          </section>
        </div>
      </transition>
    </Teleport>
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: var(--z-header);
  background: color-mix(in oklab, var(--color-white) 92%, transparent);
  backdrop-filter: saturate(150%) blur(10px);
  -webkit-backdrop-filter: saturate(150%) blur(10px);
  transition: box-shadow var(--dur-base) var(--ease-out), background var(--dur-base);
}
@media (min-width: 64rem) {
  .header { top: var(--topbar-h); }
}
.header--scrolled {
  box-shadow: 0 1px 0 var(--border), var(--shadow-sm);
}
.header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  min-height: 4.5rem;
}

.header__actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* Burger */
.header__burger {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-pill);
  background: var(--bg-soft);
}
@media (min-width: 64rem) {
  .header__burger { display: none; }
}
.burger {
  display: inline-block;
  position: relative;
  width: 1.125rem;
  height: 0.875rem;
}
.burger span {
  position: absolute;
  left: 0; right: 0;
  height: 2px;
  background: var(--color-chocolate);
  border-radius: 2px;
  transition: transform var(--dur-base) var(--ease-out), opacity var(--dur-fast);
}
.burger span:nth-child(1) { top: 0; }
.burger span:nth-child(2) { top: 50%; transform: translateY(-50%); }
.burger span:nth-child(3) { bottom: 0; }
.burger--open span:nth-child(1) { transform: translateY(0.4rem) rotate(45deg); }
.burger--open span:nth-child(2) { opacity: 0; }
.burger--open span:nth-child(3) { transform: translateY(-0.4rem) rotate(-45deg); }

/* Drawer */
.drawer {
  position: fixed;
  inset: 4.5rem 0 0 0;
  background: var(--color-white);
  padding: var(--space-5) var(--layout-gutter) var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  z-index: var(--z-drawer);
  overflow-y: auto;
}
@media (min-width: 64rem) { .drawer { display: none; } }

/* Sections : un intitulé discret + un groupe de liens, pour donner du contexte. */
.drawer__section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.drawer__section-title {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.drawer__list {
  display: flex;
  flex-direction: column;
}
.drawer__link {
  display: block;
  padding: var(--space-3) 0;
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-chocolate);
  border-bottom: 1px solid var(--border);
  transition: color var(--dur-fast) var(--ease-out);
}
.drawer__link:hover,
.drawer__link:focus-visible {
  color: var(--primary);
}

/* Switch de langue, repris de la TopBar desktop. */
.drawer__lang {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.drawer__lang-btn {
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--bg-soft);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
}
.drawer__lang-btn:hover,
.drawer__lang-btn:focus-visible {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
}
/* Langue active : pastille pleine, non cliquable. */
.drawer__lang-btn--active {
  border-color: var(--primary);
  background: var(--primary);
  color: var(--text-on-primary);
  cursor: default;
}

.drawer-enter-active, .drawer-leave-active {
  transition: opacity var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-out);
}
.drawer-enter-from, .drawer-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
